package com.xorrus.trading.lot.relayclient.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Instant

/**
 * 45DTE Strangle Swingtrade — Remote-Typisierung analog Opening Put 2.1 (Spec §3-Muster).
 *
 * Underlying ist eine fixe Strategie-Konstante (SPY, ~45DTE, Delta-5-Call + Delta-5-Put
 * als Short Strangle), ebenso Zeitfenster, TP/SL und Time-Exit — die Remote-Params
 * spiegeln exakt das, was auch die Desktop-View anbietet: Konto, Anzahl, Transmit,
 * Vorbedingungen ignorieren und optional auf das Handelsfenster warten.
 *
 * Der Strangle hat ZWEI Beine — Vorschau und Bestätigung arbeiten daher mit einer
 * Kontrakt-LISTE (Call + Put), nicht mit einem Einzelkontrakt.
 */

val FOURTY_FIVE_DTE_STRATEGY_ID = StrategyId("fourtyFiveDte")
const val FOURTY_FIVE_DTE_SCHEMA_VERSION = 1

@Serializable
@SerialName("fourtyFiveDte")
data class FourtyFiveDteParams(
    /** Konto (dynamische Auswahl, [FourtyFiveDteOptions]) */
    val accountId: AccountId,
    /** An Börse übertragen */
    val transmitToExchange: Boolean,
    /** Anzahl 1..10000 (Grenze der Strategie) */
    val quantity: Int,
    /**
     * PreCheck (Wochentag/Zeitfenster, Gap) übergehen — desktopseitig zusätzlich
     * an die Lizenz FEATURE_IGNORE_PRECONDITION gebunden (wie in der Desktop-View).
     * Teaminterne Funktion, nicht für Kunden.
     */
    val ignorePreconditions: Boolean = false,
    /**
     * erst bis 09:32 NY warten (Pendant zum „zeitgesteuert starten"-Button der View) —
     * desktopseitig an FEATURE_TIME_BASED_TRADING gebunden. Teaminterne Funktion,
     * nicht für Kunden; platziert ohne erneute Kontrakt-Bestätigung.
     */
    val waitForWindow: Boolean = false,
    /**
     * Vom Nutzer bestätigte Strangle-Beine in Symbol-Notation, aus `strategy.preview`
     * ([FourtyFiveDtePreview.contracts]). PFLICHT für den Sofort-Start (waitForWindow =
     * false): Der Desktop ermittelt die Beine beim Platzieren erneut aus Live-Preisen und
     * platziert nur bei Übereinstimmung BEIDER Kontrakte (Compliance). Bei
     * waitForWindow = true ignoriert. Nicht in Defaults/Presets persistieren —
     * die Bestätigung gilt nur für den einen Start.
     */
    val expectedContracts: List<String>? = null,
    override val schemaVersion: Int = FOURTY_FIVE_DTE_SCHEMA_VERSION,
) : StrategyParams

@Serializable
@SerialName("fourtyFiveDte")
data class FourtyFiveDteOptions(
    val accounts: List<AccountRef>,
    override val asOf: Instant,
    /** Lizenz-Freigaben der teaminternen Schalter ([ChecklistGates]); Default gesperrt. */
    override val ignorePreconditionsAllowed: Boolean = false,
    override val waitForWindowAllowed: Boolean = false,
) : StrategyOptions, ChecklistGates

/**
 * Ergebnis von `strategy.preview` (Compliance-Vorschau für den Sofort-Start): die aus
 * Live-Preisen ermittelten Strangle-Beine plus alle Orders, die platziert würden.
 * Muster analog [OpeningPutPreview], nur mit Kontrakt-Liste.
 */
@Serializable
data class FourtyFiveDtePreview(
    /** Symbol-Notation der ermittelten Beine (Call, Put); leer = Ermittlung fehlgeschlagen (siehe [message]). */
    val contracts: List<String> = emptyList(),
    /** Anzeigefertige Zeile pro Order (Entry, Take Profit, Stop Loss, Time-Exit). */
    val orders: List<String> = emptyList(),
    /** Fehlermeldung, wenn keine Kontrakte ermittelt werden konnten. */
    val message: String? = null,
    /** Zweisprachige Fassung von [message]; null bei alten Desktops. */
    val messageText: RemoteText? = null,
    val asOf: Instant,
)

@Serializable
@SerialName("fourtyFiveDte")
data class FourtyFiveDteStatus(
    override val phase: StrategyPhase,
    override val activeParams: StrategyParams? = null,
    override val since: Instant? = null,
    override val message: String? = null,
    override val messageText: RemoteText? = null,
    val steps: List<RemoteChecklistStep> = emptyList(),
    /** PreCheck wurde für diesen Lauf übersprungen */
    val preconditionsIgnored: Boolean = false,
) : RemoteStrategyStatus {
    override val strategyId: StrategyId get() = FOURTY_FIVE_DTE_STRATEGY_ID
}

// ---- Bounds -------------------------------------------------------------------------

data class FourtyFiveDteBounds(
    val maxQuantity: Int? = 10,
    val allowTransmitToExchange: Boolean = false,
) : ParamsBounds<FourtyFiveDteParams> {

    override fun violations(params: FourtyFiveDteParams): List<String> = violationsText(params).map { it.de }

    override fun violationsText(params: FourtyFiveDteParams): List<RemoteText> {
        val violations = mutableListOf<RemoteText>()
        if (maxQuantity != null && params.quantity > maxQuantity) {
            violations += RemoteText(
                de = "quantity ${params.quantity} überschreitet das Limit $maxQuantity",
                en = "quantity ${params.quantity} exceeds the limit $maxQuantity",
            )
        }
        if (params.transmitToExchange && !allowTransmitToExchange) {
            violations += RemoteText(
                de = "transmitToExchange ist für Remote-Aktivierungen nicht freigegeben",
                en = "transmitToExchange is not enabled for remote activations",
            )
        }
        return violations
    }
}

// ---- Geteilte Validierung (§3.2) ------------------------------------------------------

fun validateFourtyFiveDte(params: FourtyFiveDteParams, options: FourtyFiveDteOptions?): List<ValidationError> {
    val errors = mutableListOf<ValidationError>()

    if (params.schemaVersion != FOURTY_FIVE_DTE_SCHEMA_VERSION) {
        errors += ValidationError(
            field = "schemaVersion",
            message = "Unbekannte schemaVersion ${params.schemaVersion} (unterstützt: $FOURTY_FIVE_DTE_SCHEMA_VERSION) — Controller-App aktualisieren",
            code = ValidationCode.UNSUPPORTED_SCHEMA,
            messageText = RemoteText(
                de = "Unbekannte schemaVersion ${params.schemaVersion} (unterstützt: $FOURTY_FIVE_DTE_SCHEMA_VERSION) — Controller-App aktualisieren",
                en = "Unknown schemaVersion ${params.schemaVersion} (supported: $FOURTY_FIVE_DTE_SCHEMA_VERSION) — update the controller app",
            ),
        )
    }
    if (params.quantity !in 1..10000) {
        errors += ValidationError(
            "quantity",
            "Anzahl muss zwischen 1 und 10000 liegen",
            messageText = RemoteText(
                de = "Anzahl muss zwischen 1 und 10000 liegen",
                en = "Quantity must be between 1 and 10000",
            ),
        )
    }
    if (params.expectedContracts != null && params.expectedContracts.size != 2) {
        errors += ValidationError(
            "expectedContracts",
            "Bestätigung muss genau zwei Kontrakte (Call + Put) enthalten",
            messageText = RemoteText(
                de = "Bestätigung muss genau zwei Kontrakte (Call + Put) enthalten",
                en = "Confirmation must contain exactly two contracts (call + put)",
            ),
        )
    }
    if (options != null && options.accounts.none { it.id == params.accountId }) {
        errors += ValidationError(
            field = "accountId",
            message = "Das gewählte Konto ist nicht (mehr) verfügbar — Auswahl neu laden",
            code = ValidationCode.STALE_OPTION,
            messageText = RemoteText(
                de = "Das gewählte Konto ist nicht (mehr) verfügbar — Auswahl neu laden",
                en = "The selected account is not (or no longer) available — reload the selection",
            ),
        )
    }
    errors += checklistGateErrors(params.ignorePreconditions, params.waitForWindow, options)
    return errors
}
